import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Play } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, ugx } from "@/lib/format";
import { PageHeader, Panel, Empty } from "@/components/Shell";

const blank = {
  name: "", position: "", email: "", phone: "", tin: "", nssf_number: "",
  gross_salary: 0, allowances: 0, bank_name: "", bank_account: "", active: true,
};

export default function Payroll() {
  const { canWrite } = useAuth();
  const [tab, setTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));

  const load = () => {
    api.get("/payroll/employees").then(({ data }) => setEmployees(data));
    api.get("/payroll/runs").then(({ data }) => setRuns(data));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, gross_salary: Number(form.gross_salary), allowances: Number(form.allowances) };
    try {
      if (editing) await api.put(`/payroll/employees/${editing}`, payload);
      else await api.post("/payroll/employees", payload);
      toast.success("Employee saved");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/payroll/employees/${id}`); toast.success("Employee removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const runPayroll = async () => {
    try {
      await api.post("/payroll/runs", { period });
      toast.success(`Payroll for ${period} processed and posted`);
      setTab("runs"); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteRun = async (id) => {
    try { await api.delete(`/payroll/runs/${id}`); toast.success("Payroll run reversed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="payroll-page">
      <PageHeader title="Payroll" subtitle="Uganda PAYE bands, NSSF 5% employee / 10% employer, all in UGX."
        action={canWrite && tab === "employees" && (
          <button data-testid="add-employee-button" onClick={() => { setForm(blank); setEditing(null); setOpen(true); }}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> New employee
          </button>
        )} />

      <div className="mb-4 flex gap-2">
        {[["employees", "Employees"], ["runs", "Payroll runs"]].map(([k, label]) => (
          <button key={k} data-testid={`payroll-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
              tab === k ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "employees" ? (
        <Panel title={`${employees.length} employees`}>
          {employees.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="employees-table">
                <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="px-5 py-2.5">Name</th><th className="px-5 py-2.5">Position</th>
                    <th className="px-5 py-2.5">TIN</th><th className="px-5 py-2.5">NSSF No.</th>
                    <th className="px-5 py-2.5 text-right">Gross</th><th className="px-5 py-2.5 text-right">Allowances</th>
                    <th className="px-5 py-2.5 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {employees.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-slate-900">{e.name}</td>
                      <td className="px-5 py-3 text-slate-600">{e.position || "—"}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-500">{e.tin || "—"}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-500">{e.nssf_number || "—"}</td>
                      <td className="px-5 py-3 text-right tabular-nums font-medium">{money(e.gross_salary)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-600">{money(e.allowances)}</td>
                      <td className="px-5 py-3 text-right">
                        {canWrite && (
                          <div className="flex justify-end gap-2">
                            <button data-testid="edit-employee-button" onClick={() => { setForm({ ...blank, ...e }); setEditing(e.id); setOpen(true); }}
                              className="text-xs font-semibold text-emerald-700 hover:underline">Edit</button>
                            <button data-testid="delete-employee-button" onClick={() => remove(e.id)}
                              className="text-slate-400 hover:text-rose-600" aria-label="Delete employee"><Trash2 size={15} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty message="No employees added yet." />}
        </Panel>
      ) : (
        <div className="space-y-6">
          {canWrite && (
            <Panel title="Process payroll">
              <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Period</label>
                  <input data-testid="payroll-period-input" type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
                <button data-testid="run-payroll-button" onClick={runPayroll}
                  className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                  <Play size={15} /> Run payroll for all active employees
                </button>
              </div>
            </Panel>
          )}
          {runs.length ? runs.map((r) => (
            <Panel key={r.id} title={`Payroll ${r.period} · paid ${shortDate(r.pay_date)}`}
              right={canWrite && (
                <button data-testid="delete-payroll-run-button" onClick={() => deleteRun(r.id)} className="text-slate-400 hover:text-rose-600" aria-label="Reverse run">
                  <Trash2 size={15} />
                </button>
              )}>
              <div className="grid grid-cols-2 gap-3 border-b border-slate-200 p-5 sm:grid-cols-5">
                {[["Gross", r.totals.gross], ["PAYE", r.totals.paye], ["NSSF employee", r.totals.nssf_employee],
                  ["NSSF employer", r.totals.nssf_employer], ["Net pay", r.totals.net_pay]].map(([label, v]) => (
                  <div key={label}>
                    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="font-display text-base font-bold text-slate-900">{ugx(v)}</p>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payslips-table">
                  <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-5 py-2">Employee</th><th className="px-5 py-2 text-right">Gross</th>
                      <th className="px-5 py-2 text-right">NSSF 5%</th><th className="px-5 py-2 text-right">PAYE</th>
                      <th className="px-5 py-2 text-right">Net pay</th></tr>
                  </thead>
                  <tbody>
                    {r.payslips.map((p) => (
                      <tr key={p.employee_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                        <td className="px-5 py-2 font-medium text-slate-800">{p.employee_name}</td>
                        <td className="px-5 py-2 text-right tabular-nums">{money(p.gross)}</td>
                        <td className="px-5 py-2 text-right tabular-nums text-slate-600">{money(p.nssf_employee)}</td>
                        <td className="px-5 py-2 text-right tabular-nums text-slate-600">{money(p.paye)}</td>
                        <td className="px-5 py-2 text-right tabular-nums font-semibold">{money(p.net_pay)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )) : <Panel><Empty message="No payroll has been processed yet." /></Panel>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
          <div className="mx-auto w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-md" data-testid="employee-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">{editing ? "Edit employee" : "New employee"}</h3>
              <button data-testid="close-employee-dialog" onClick={() => setOpen(false)} className="text-slate-400"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4 p-5">
              {[["Full name", "name", "text", true], ["Position", "position", "text", false],
                ["Email", "email", "email", false], ["Phone", "phone", "text", false],
                ["TIN", "tin", "text", false], ["NSSF number", "nssf_number", "text", false],
                ["Gross salary (UGX)", "gross_salary", "number", true], ["Allowances (UGX)", "allowances", "number", false],
                ["Bank name", "bank_name", "text", false], ["Bank account", "bank_account", "text", false]].map(([label, key, type, req]) => (
                <div key={key}>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
                  <input data-testid={`employee-${key}-input`} type={type} required={req} value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500" />
                </div>
              ))}
              <button data-testid="save-employee-button" className="col-span-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">Save employee</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
